import { useDataGrid, List, ShowButton } from '@refinedev/mui'
import { useNavigation } from '@refinedev/core'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Typography, Chip, Link, Box } from '@mui/material'
import { useLocation } from 'react-router-dom'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LaunchIcon from '@mui/icons-material/Launch'
import OrgsQuickSearch from '../../components/OrgsQuickSearch'
import { PaginationControls } from '../../components/PaginationControls'

export const OrgsList: React.FC = () => {
  const { show } = useNavigation()
  const location = useLocation()

  // Build filters from URL parameters for server-side filtering
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(location.search)
    const filters: any[] = []

    // Handle active filter
    if (params.get('active') !== null) {
      filters.push({
        field: 'active',
        operator: 'eq',
        value: params.get('active') === 'true',
      })
    }

    // Handle other filters
    if (params.get('flagged') === 'true') {
      filters.push({
        field: 'flag_for_updates',
        operator: 'nnull',
        value: true,
      })
    }
    if (params.get('membership')) {
      filters.push({
        field: 'membership',
        operator: 'eq',
        value: params.get('membership'),
      })
    }
    if (params.get('payment_type')) {
      filters.push({
        field: 'payment_type',
        operator: 'eq',
        value: params.get('payment_type'),
      })
    }
    if (params.get('billing_frequency')) {
      filters.push({
        field: 'billing_frequency',
        operator: 'eq',
        value: params.get('billing_frequency'),
      })
    }
    if (params.get('orgType')) {
      filters.push({
        field: 'orgType',
        operator: 'eq',
        value: params.get('orgType'),
      })
    }
    if (params.get('days_inactive')) {
      filters.push({
        field: 'days_inactive',
        operator: 'eq',
        value: params.get('days_inactive'),
      })
    }
    if (params.get('has_never_scanned') === 'true') {
      filters.push({
        field: 'has_never_scanned',
        operator: 'eq',
        value: true,
      })
    }

    // Handle quick search - default to searching name field since search_type dropdown was removed
    const quickSearch = params.get('q')
    if (quickSearch) {
      filters.push({
        field: 'name',
        operator: 'contains',
        value: quickSearch,
      })
    }

    return filters
  }

  const { dataGridProps, current, setCurrent, pageSize, setPageSize } = useDataGrid({
    resource: 'orgs',
    filters: {
      permanent: getFiltersFromURL(),
    },
    sorters: {
      initial: [
        {
          field: 'created_at',
          order: 'desc',
        },
      ],
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting orgs',
        description: error?.message || 'An error occurred while fetching orgs',
        type: 'error',
      }
    },
  })

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      filterable: true,
    },
    {
      field: 'active',
      headerName: 'Active',
      flex: 0.25,
      minWidth: 65,
      align: 'center',
      headerAlign: 'center',
      filterable: true,
      type: 'boolean',
      valueGetter: (params) => params.row.active,
      renderCell: function render({ row }) {
        return (
          <Typography>
            {row.active ? (
              <CheckCircleIcon color="success" fontSize="inherit" />
            ) : (
              <CancelIcon color="error" fontSize="inherit" />
            )}
          </Typography>
        )
      },
    },
    {
      field: 'membership',
      headerName: 'Membership',
      flex: 0.25,
      minWidth: 100,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Paid', 'Free', 'None'],
      valueGetter: (params) => params.row.membership || 'None',
      renderCell: function render({ row }) {
        const membershipColor = row.membership === 'Paid' ? 'success' :
                               row.membership === 'Free' ? 'primary' : 'default'
        return (
          <Chip
            label={row.membership || 'None'}
            color={membershipColor}
            size="small"
          />
        )
      },
    },
    {
      field: 'tier',
      headerName: 'Tier',
      flex: 0.15,
      minWidth: 60,
      align: 'center',
      headerAlign: 'center',
      filterable: true,
      type: 'number',
      valueFormatter: function format({ value }) {
        if (value === 0) return 'Tier Micro'
        if (value === -1) return 'Custom'
        if (value === null || value === undefined) return 'N/A'
        return `Tier ${value}`
      },
    },
    {
      field: 'payment_type',
      headerName: 'Payment Type',
      flex: 0.25,
      minWidth: 120,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Invoice', 'Stripe', 'None'],
      valueGetter: (params) => params.row.payment_type || 'None',
      renderCell: function render({ row }) {
        const getPaymentColor = (type: string) => {
          switch (type) {
            case 'Invoice': return 'warning'
            case 'Stripe': return 'info'
            default: return 'default'
          }
        }

        return (
          <Chip
            label={row.payment_type || 'None'}
            color={getPaymentColor(row.payment_type)}
            size="small"
            variant="outlined"
          />
        )
      },
    },
    {
      field: 'billing_frequency',
      headerName: 'Billing Frequency',
      flex: 0.25,
      minWidth: 130,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Monthly', 'Quarterly', 'Yearly', 'N/A'],
      valueGetter: (params) => params.row.billing_frequency || 'N/A',
      valueFormatter: function format({ value }) {
        return value || 'N/A'
      },
    },
    {
      field: 'billing_amount',
      headerName: 'Billing Amount',
      flex: 0.25,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      type: 'string',
      filterable: true,
      valueGetter: function getValue({ row }) {
        if (!row.billing_amount || row.billing_amount === '$0' || row.billing_amount === '0') {
          return 'Tier pricing'
        }
        const value = row.billing_amount.toString()
        return value.startsWith('$') ? value : `$${value}`
      },
      valueFormatter: function format({ value }) {
        return value || 'Tier pricing'
      },
    },
    {
      field: 'xero_contact_id',
      headerName: 'Xero Connection',
      flex: 0.25,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      filterable: true,
      type: 'boolean',
      valueGetter: (params) => !!params.row.xero_contact_id,
      renderCell: function render({ row }) {
        if (row.xero_contact_id) {
          return (
            <Link
              href={`https://go.xero.com/app/!r56tr/contacts/contact/${row.xero_contact_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <CheckCircleIcon color="success" fontSize="small" />
              <LaunchIcon fontSize="small" />
            </Link>
          )
        } else if (row.payment_type === 'Stripe') {
          return (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          )
        } else {
          return <CancelIcon color="error" fontSize="small" />
        }
      },
    },
    {
      field: 'has_recurring_invoice',
      headerName: 'Recurring',
      flex: 0.15,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      filterable: true,
      type: 'boolean',
      valueGetter: (params) => {
        if (params.row.payment_type === 'Stripe' || !params.row.xero_contact_id) {
          return null
        }
        return params.row.has_recurring_invoice
      },
      renderCell: function render({ row }) {
        if (row.payment_type === 'Stripe' || !row.xero_contact_id) {
          return (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          )
        }
        return (
          <Typography>
            {row.has_recurring_invoice ? 'Yes' : 'No'}
          </Typography>
        )
      },
    },
    {
      field: 'created_at',
      headerName: 'Created Date',
      flex: 0.25,
      align: 'left',
      minWidth: 200,
      filterable: true,
      type: 'date',
      valueGetter: (params) => {
        return params.row.created_at ? new Date(params.row.created_at) : null
      },
      valueFormatter: function format({ value }) {
        if (!value) return 'No date available'
        const date = new Date(value)
        const utcDate = new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
          )
        )
        const formattedDate = `${utcDate.toLocaleString('en-NZ', {
          hour12: true,
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Pacific/Auckland',
        })}`
        return formattedDate
      },
    },
    {
      field: 'orgType',
      headerName: 'Umbrella Orgs',
      flex: 0.25,
      minWidth: 250,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Umbrella Org', 'Sub Org', 'Standalone'],
      valueGetter: (params) => {
        if (params.row.has_sub_orgs) {
          return 'Umbrella Org'
        } else if (params.row.is_child_of_uuid) {
          return 'Sub Org'
        } else {
          return 'Standalone'
        }
      },
      renderCell: function render({ row }) {
        if (row.has_sub_orgs) {
          return <Typography>Umbrella Org</Typography>
        } else if (row.is_child_of_uuid) {
          return <Typography>Sub Org</Typography>
        } else {
          return <Typography>Standalone</Typography>
        }
      },
    },
    {
      field: 'last_activity_date',
      headerName: 'Last Activity',
      flex: 0.25,
      minWidth: 120,
      filterable: false,
      sortable: true,
      valueGetter: (params) => {
        return params.row.last_activity_date ? new Date(params.row.last_activity_date) : null
      },
      renderCell: function render({ row }) {
        if (!row.last_activity_date) {
          return (
            <Typography variant="body2" color="error">
              Never
            </Typography>
          )
        }
        const now = new Date()
        const activity = new Date(row.last_activity_date)
        const diffDays = Math.floor((now.getTime() - activity.getTime()) / (1000 * 60 * 60 * 24))
        const color = diffDays > 90 ? 'error' : diffDays > 30 ? 'warning.main' : 'text.primary'
        return (
          <Typography variant="body2" sx={{ color }}>
            {diffDays}d ago
          </Typography>
        )
      },
    },
    {
      field: 'total_scans',
      headerName: 'Scans',
      flex: 0.15,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      sortable: true,
      type: 'number',
      valueGetter: (params) => params.row.total_scans || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.25,
      filterable: false,
      sortable: false,
      renderCell: function render({ row }) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ShowButton hideText recordItemId={row.uuid} />
          </div>
        )
      },
      align: 'center',
      headerAlign: 'center',
      minWidth: 80,
    },
  ]

  const handleRowClick = (params: any) => {
    show('orgs', params.row.uuid)
  }

  return (
    <List>
      <OrgsQuickSearch />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <PaginationControls
          currentPage={current}
          pageSize={pageSize}
          totalCount={dataGridProps.rowCount || 0}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={setCurrent}
          onPageSizeChange={setPageSize}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        pageSizeOptions={[10, 25, 50, 100]}
        getRowId={(row) => row.uuid}
        disableColumnFilter={false}
        onRowClick={handleRowClick}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    </List>
  )
}
